// The homepage's sky: sandstone dusk above a sea of cloud. A few hundred cloud puffs, each a camera-facing sprite,
// drawn with WebGL2 (no engine: the landing page must open fast). Each puff is one of four lumpy shapes with its own
// self-shadow baked in, lit like the sun has just gone under the horizon: gold underneath, dark earth on top, a glow
// towards the sun, and haze with distance. Two canvases share one field of clouds: the one behind the wordmark draws
// the puffs beyond it, the one in front draws the rest, so the words hang between clouds. The page moves the camera
// (scroll flies you through, the mouse leans); this file only draws.

export interface Cam {
  x: number;
  y: number;
  z: number;
  /** radians: negative looks down */
  pitch: number;
  yaw: number;
  /** vertical field of view, radians */
  fov: number;
}

/** 8 floats a puff: x y z size · rotation shade alpha shape (0–3, +4 when mirrored) */
const STRIDE = 8;

/** a small seeded random number generator, so both canvases (and every visit) get the same sky */
export function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The field of clouds round the wordmark (which stands at z = `mark`, the camera starting at z = 0, eye level y = 0):
 * a sea of cloud below eye level all the way to the horizon, tall towers rising out of it either side of the words,
 * a few wisps low in front (they brush the letters, never hide them), and thin high streaks.
 */
export function makeField(count: number, mark: number, seed = 23): Float32Array {
  const r = rng(seed);
  const out: number[] = [];
  const between = (a: number, b: number) => a + (b - a) * r();
  const puff = (x: number, y: number, z: number, size: number, alpha: number) =>
    out.push(x, y, z, size, (r() - 0.5) * 0.7, r(), alpha, Math.floor(r() * 4) + (r() < 0.5 ? 4 : 0));
  const budget = (f: number) => Math.round(count * f);
  // the sea of cloud, near: lumpy clusters just below eye level
  for (let n = budget(0.34); n > 0; ) {
    const k = Math.min(n, 5 + Math.floor(r() * 5));
    const cx = between(-90, 90);
    const cz = between(-130, -12);
    const cy = between(-15, -10);
    for (let i = 0; i < k; i++) puff(cx + between(-9, 9), cy + between(0, 3.5), cz + between(-6, 6), between(5, 10), between(0.55, 0.9));
    n -= k;
  }
  // the sea of cloud, far: big single puffs out to the horizon
  for (let i = budget(0.28); i > 0; i--) puff(between(-200, 200), between(-16, -9), between(-320, -110), between(12, 22), between(0.5, 0.85));
  // towers rising out of the sea either side of the words, and behind them
  for (let n = budget(0.24); n > 0; ) {
    const k = Math.min(n, 9 + Math.floor(r() * 8));
    const side = r() < 0.5 ? -1 : 1;
    const behind = r() < 0.3;
    const cx = behind ? between(-30, 30) : side * between(18, 64);
    const cz = behind ? between(mark - 90, mark - 40) : between(-170, -24);
    const top = behind ? between(-4, 1) : between(0, 9);
    for (let i = 0; i < k; i++) {
      // a tower: wide at the base in the sea, narrowing as it climbs
      const v = r();
      const y = -11 + v * (top + 11);
      const w = (1 - v * 0.6) * between(5, 9);
      puff(cx + between(-w, w), y, cz + between(-5, 5), between(4.5, 9) * (1 - v * 0.3), between(0.6, 0.92));
    }
    n -= k;
  }
  // wisps in front: low, either side, see-through
  for (let i = budget(0.05); i > 0; i--) {
    const side = r() < 0.5 ? -1 : 1;
    puff(side * between(7, 26), between(-9, -4.5), between(mark + 8, -7), between(4, 8), between(0.18, 0.35));
  }
  // high streaks
  for (let i = budget(0.09); i > 0; i--) puff(between(-160, 160), between(10, 20), between(-280, -40), between(10, 18), between(0.1, 0.22));
  return new Float32Array(out);
}

/**
 * Four lumpy puffs in a 2×2 atlas. Alpha: how much cloud; colour: its self-shadow, every lump brighter on its
 * underside (the light comes from below) and darker where lumps pile up behind each other.
 */
function puffAtlas(cell = 128) {
  const c = document.createElement('canvas');
  c.width = c.height = cell * 2;
  const g = c.getContext('2d')!;
  const shade = document.createElement('canvas');
  shade.width = shade.height = cell;
  const s = shade.getContext('2d')!;
  const mask = document.createElement('canvas');
  mask.width = mask.height = cell;
  const m = mask.getContext('2d')!;
  for (let v = 0; v < 4; v++) {
    const r = rng(101 + v * 17);
    const lumps: [number, number, number][] = [];
    for (let i = 0; i < 16 + v * 3; i++) {
      // a cauliflower: lumps along a low dome, the big ones at the base
      const a = Math.PI * (0.05 + r() * 0.9);
      const d = (0.12 + r() * 0.2) * cell;
      const x = cell / 2 + Math.cos(a) * d * 1.25 * (r() < 0.5 ? -1 : 1);
      const y = cell * 0.62 - Math.sin(a) * d;
      lumps.push([x, y, cell * (0.07 + r() * 0.1) * (1.25 - (cell * 0.62 - y) / cell)]);
    }
    lumps.push([cell / 2, cell * 0.6, cell * 0.2]);
    // the density
    m.clearRect(0, 0, cell, cell);
    m.globalCompositeOperation = 'lighter';
    for (const [x, y, rad] of lumps) {
      const grd = m.createRadialGradient(x, y, 0, x, y, rad);
      grd.addColorStop(0, 'rgba(255,255,255,0.75)');
      grd.addColorStop(0.62, 'rgba(255,255,255,0.45)');
      grd.addColorStop(1, 'rgba(255,255,255,0)');
      m.fillStyle = grd;
      m.beginPath();
      m.arc(x, y, rad, 0, Math.PI * 2);
      m.fill();
    }
    m.globalCompositeOperation = 'destination-in';
    const fade = m.createRadialGradient(cell / 2, cell / 2, cell * 0.2, cell / 2, cell / 2, cell * 0.5);
    fade.addColorStop(0, 'rgba(0,0,0,1)');
    fade.addColorStop(1, 'rgba(0,0,0,0)');
    m.fillStyle = fade;
    m.fillRect(0, 0, cell, cell);
    // the self-shadow: mid grey, each lump lit from below
    s.globalCompositeOperation = 'source-over';
    s.clearRect(0, 0, cell, cell);
    s.fillStyle = '#5a5a5a';
    s.fillRect(0, 0, cell, cell);
    for (const [x, y, rad] of lumps) {
      const grd = s.createRadialGradient(x, y + rad * 0.45, 0, x, y + rad * 0.2, rad * 1.05);
      grd.addColorStop(0, 'rgba(255,255,255,0.85)');
      grd.addColorStop(0.55, 'rgba(200,200,200,0.35)');
      grd.addColorStop(1, 'rgba(40,40,40,0.25)');
      s.fillStyle = grd;
      s.beginPath();
      s.arc(x, y, rad * 1.05, 0, Math.PI * 2);
      s.fill();
    }
    // the shade kept only inside the density (composed on its own canvas: destination-in clears everything else)
    s.globalCompositeOperation = 'destination-in';
    s.drawImage(mask, 0, 0);
    g.drawImage(shade, (v % 2) * cell, Math.floor(v / 2) * cell);
  }
  return c;
}

const VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aCorner;
layout(location = 1) in vec4 aPos;   // x y z size
layout(location = 2) in vec4 aInfo;  // rotation shade alpha shape
uniform mat4 uView;
uniform mat4 uProj;
uniform vec3 uSun;
uniform vec2 uFog;
out vec2 vUv;
out vec4 vCol;
out float vSun;
void main() {
  vec4 vp = uView * vec4(aPos.xyz, 1.0);
  vp.xy += aCorner * aPos.w;
  gl_Position = uProj * vp;
  // a small turn and maybe a mirror, so the light stays underneath
  float c = cos(aInfo.x), s = sin(aInfo.x);
  vec2 k = vec2(c * aCorner.x - s * aCorner.y, s * aCorner.x + c * aCorner.y);
  float shape = mod(aInfo.w, 4.0);
  if (aInfo.w >= 4.0) k.x = -k.x;
  vec2 cell = vec2(mod(shape, 2.0), floor(shape / 2.0));
  vUv = (clamp(vec2(k.x, -k.y) * 0.5 + 0.5, 0.0, 1.0) + cell) * 0.5;
  float d = -vp.z;
  // puffs right in front of the lens melt away instead of showing their edges
  float near = smoothstep(2.5, 15.0, d);
  float far = 1.0 - smoothstep(uFog.x, uFog.y, d);
  // sandstone dusk: gold under, dark earth on top
  float h = clamp(aCorner.y * 0.5 + 0.5, 0.0, 1.0);
  vec3 under = mix(vec3(1.0, 0.68, 0.36), vec3(1.0, 0.56, 0.3), aInfo.y);
  vec3 top = mix(vec3(0.27, 0.15, 0.1), vec3(0.36, 0.2, 0.13), aInfo.y);
  vec3 col = mix(under, top, smoothstep(0.08, 0.92, h));
  vSun = pow(max(dot(normalize(vp.xyz), uSun), 0.0), 4.0);
  col = mix(col, vec3(0.25, 0.11, 0.06), smoothstep(uFog.x * 0.3, uFog.y, d) * 0.75);
  vCol = vec4(col, aInfo.z * near * far);
}`;

const FS = `#version 300 es
precision mediump float;
in vec2 vUv;
in vec4 vCol;
in float vSun;
uniform sampler2D uTex;
out vec4 o;
void main() {
  vec4 t = texture(uTex, vUv);
  float a = smoothstep(0.03, 0.55, t.a) * vCol.a;
  if (a < 0.004) discard;
  // the baked self-shadow shapes the lumps; the sun lights the edges facing it
  vec3 col = vCol.rgb * (0.42 + 0.9 * t.r) + vec3(1.0, 0.7, 0.38) * vSun * (0.35 + 0.65 * (1.0 - t.a));
  o = vec4(col * a, a);
}`;

function shader(gl: WebGL2RenderingContext, type: number, src: string) {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'shader');
  return s;
}

/** column-major 4×4 helpers */
function perspective(fov: number, aspect: number, near: number, far: number) {
  const f = 1 / Math.tan(fov / 2);
  const nf = 1 / (near - far);
  return new Float32Array([f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0]);
}
/** the view: turn by yaw, tip by pitch, after moving the world by −camera */
function view(c: Cam) {
  const cp = Math.cos(c.pitch);
  const sp = Math.sin(c.pitch);
  const cy = Math.cos(c.yaw);
  const sy = Math.sin(c.yaw);
  // R = Rx(−pitch) · Ry(−yaw): the world turns the other way to the camera (pitch > 0 looks up)
  const r00 = cy;
  const r01 = 0;
  const r02 = -sy;
  const r10 = sp * sy;
  const r11 = cp;
  const r12 = sp * cy;
  const r20 = cp * sy;
  const r21 = -sp;
  const r22 = cp * cy;
  const tx = -(r00 * c.x + r01 * c.y + r02 * c.z);
  const ty = -(r10 * c.x + r11 * c.y + r12 * c.z);
  const tz = -(r20 * c.x + r21 * c.y + r22 * c.z);
  return new Float32Array([r00, r10, r20, 0, r01, r11, r21, 0, r02, r12, r22, 0, tx, ty, tz, 1]);
}

/** a world point in the camera's view: x right, y up, and its distance ahead (≤ 0 is behind the camera) */
export function toView(c: Cam, x: number, y: number, z: number) {
  const V = view(c);
  return { x: V[0] * x + V[4] * y + V[8] * z + V[12], y: V[1] * x + V[5] * y + V[9] * z + V[13], depth: -(V[2] * x + V[6] * y + V[10] * z + V[14]) };
}

class Layer {
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private inst: WebGLBuffer;
  private data: Float32Array;
  private u: Record<string, WebGLUniformLocation | null> = {};
  constructor(
    readonly canvas: HTMLCanvasElement,
    private field: Float32Array,
  ) {
    const gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, powerPreference: 'low-power' });
    if (!gl) throw new Error('no WebGL2');
    this.gl = gl;
    const p = gl.createProgram()!;
    gl.attachShader(p, shader(gl, gl.VERTEX_SHADER, VS));
    gl.attachShader(p, shader(gl, gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) ?? 'link');
    this.prog = p;
    for (const k of ['uView', 'uProj', 'uSun', 'uFog', 'uTex']) this.u[k] = gl.getUniformLocation(p, k);
    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.data = new Float32Array(field.length);
    this.inst = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.inst);
    gl.bufferData(gl.ARRAY_BUFFER, this.data.byteLength, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 4, gl.FLOAT, false, STRIDE * 4, 0);
    gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 4, gl.FLOAT, false, STRIDE * 4, 16);
    gl.vertexAttribDivisor(2, 1);
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, puffAtlas());
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.useProgram(p);
    gl.uniform1i(this.u.uTex, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  }

  /** draw the puffs `keep` says are this layer's, farthest first */
  draw(c: Cam, t: number, keep: (z: number) => boolean, scratch: { idx: Uint32Array; depth: Float32Array }) {
    const gl = this.gl;
    const { width: w, height: h } = this.canvas;
    gl.viewport(0, 0, w, h);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    const V = view(c);
    const f = this.field;
    const count = f.length / STRIDE;
    // the wind carries everything slowly to the right, wrapping round far out of sight
    const wind = t * 0.5;
    let n = 0;
    for (let i = 0; i < count; i++) {
      const o = i * STRIDE;
      const z = f[o + 2];
      if (!keep(z)) continue;
      let x = f[o] + wind * (0.7 + f[o + 5] * 0.6);
      x = ((((x + 210) % 420) + 420) % 420) - 210;
      const y = f[o + 1];
      const vz = V[2] * x + V[6] * y + V[10] * z + V[14];
      if (vz > -0.6) continue;
      scratch.idx[n] = i;
      scratch.depth[i] = vz;
      scratch.depth[count + i] = x;
      n++;
    }
    const idx = scratch.idx.subarray(0, n);
    idx.sort((a, b) => scratch.depth[a] - scratch.depth[b]);
    const d = this.data;
    for (let k = 0; k < n; k++) {
      const i = idx[k];
      const o = i * STRIDE;
      const q = k * STRIDE;
      d[q] = scratch.depth[count + i];
      d[q + 1] = f[o + 1];
      d[q + 2] = f[o + 2];
      d[q + 3] = f[o + 3];
      d[q + 4] = f[o + 4];
      d[q + 5] = f[o + 5];
      d[q + 6] = f[o + 6];
      d[q + 7] = f[o + 7];
    }
    gl.useProgram(this.prog);
    gl.uniformMatrix4fv(this.u.uView, false, V);
    gl.uniformMatrix4fv(this.u.uProj, false, perspective(c.fov, w / Math.max(1, h), 0.5, 500));
    // the sun, just under the horizon straight ahead, in view space
    const sx = 0;
    const sy = -0.06;
    const sz = -1;
    const vx = V[0] * sx + V[4] * sy + V[8] * sz;
    const vy = V[1] * sx + V[5] * sy + V[9] * sz;
    const vzz = V[2] * sx + V[6] * sy + V[10] * sz;
    const l = Math.hypot(vx, vy, vzz) || 1;
    gl.uniform3f(this.u.uSun, vx / l, vy / l, vzz / l);
    gl.uniform2f(this.u.uFog, 90, 330);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.inst);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, d, 0, n * STRIDE);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, n);
  }

  dispose() {
    this.gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}

export class Sky {
  private back: Layer;
  private front: Layer;
  private scratch: { idx: Uint32Array; depth: Float32Array };
  /** the camera; the page moves it */
  cam: Cam = { x: 0, y: 0, z: 0, pitch: 0, yaw: 0, fov: (55 * Math.PI) / 180 };

  constructor(
    back: HTMLCanvasElement,
    front: HTMLCanvasElement,
    /** where the wordmark stands */
    readonly mark: number,
    count: number,
  ) {
    const field = makeField(count, mark);
    this.back = new Layer(back, field);
    this.front = new Layer(front, field);
    const n = field.length / STRIDE;
    this.scratch = { idx: new Uint32Array(n), depth: new Float32Array(n * 2) };
  }

  /** canvas pixels: soft clouds don't need full resolution */
  resize(w: number, h: number, scale: number) {
    for (const l of [this.back, this.front]) {
      const cw = Math.max(1, Math.round(w * scale));
      const ch = Math.max(1, Math.round(h * scale));
      if (l.canvas.width !== cw || l.canvas.height !== ch) {
        l.canvas.width = cw;
        l.canvas.height = ch;
      }
    }
  }

  render(t: number) {
    const passed = this.cam.z < this.mark;
    // in front of the wordmark is nearer the camera; once the camera is through, everything is "in front"
    this.back.draw(this.cam, t, (z) => !passed && z <= this.mark, this.scratch);
    this.front.draw(this.cam, t, (z) => passed || z > this.mark, this.scratch);
  }

  dispose() {
    this.back.dispose();
    this.front.dispose();
  }
}
